import { useCallback, useContext, useEffect } from 'react'

import type {
  GetStaticPaths,
  GetStaticPropsContext,
  GetStaticPropsResult,
  NextPage,
} from 'next'
import dynamic from 'next/dynamic'
import { useLocale, useTranslations } from 'next-intl'

import { indexes } from '@mab-common/utils'

import { joinURL } from 'ufo'

import type { ArtworkCardProps } from '~/components/atoms/ArtworkCard/ArtworkCard'
import ArtworksSlider from '~/components/atoms/ArtworksSlider/ArtworksSlider'
import type { BreadcrumbProps } from '~/components/atoms/Breadcrumb/Breadcrumb'
import Breadcrumb from '~/components/atoms/Breadcrumb/Breadcrumb'
import Container, { gutterTypes } from '~/components/atoms/Container/Container'
import type { ImageProps } from '~/components/atoms/Image/Image'
import { sanityUrl } from '~/components/atoms/Image/Image'
import type { KeyMessageBannerProps } from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import KeyMessageBanner, {
  ImageAlignmentVariants,
} from '~/components/atoms/KeyMessageBanner/KeyMessageBanner'
import VerticalPadder from '~/components/atoms/VerticalPadder/VerticalPadder'
import AuctionHistory from '~/components/blocks/AuctionHistory/AuctionHistory'
import CatalogueEssay from '~/components/blocks/CatalogueEssay/CatalogueEssay'
import type { PDPHeroProps } from '~/components/blocks/PDPHero/PDPHero'
import PDPHero, {
  getDimensionString,
} from '~/components/blocks/PDPHero/PDPHero'
import RelatedArtworks from '~/components/blocks/RelatedArtworks/RelatedArtworks'
import { resolveBlock } from '~/components/serializers'
import { GlobalStoreContext } from '~/contexts/GlobalStoreContext'
import { SelectedArtworkContext } from '~/contexts/SelectedArtworkContext'
import { CurrentArtworkProps } from '~/contexts/SelectedArtworkReducer'
import { useSettingsContext } from '~/contexts/settingsContext'
import { FormTypes } from '~/data/forms/shared/interfaces/Form'
import type { AuctionHistoryItem } from '~/hooks/useRomeoArtworkHistory'
import useSearchAnalyst, {
  SearchAnalystEventNames,
  SearchAnalystMethods,
} from '~/services/elastic/analyst'
import type { Block } from '~/services/sanity'
import { sanityClientCdn, useSanity } from '~/services/sanity'
import toPlainText from '~/services/toPlainText'
import type { SiteSettings } from '~/types/siteSettings'
import { getPageBuilderArtistIndexData } from '~/utils/getBodyArtistIndexData'
import { getLocalizedHardCodedSlug } from '~/utils/getLocalizedHardCodedSlug'
import { getRomeoToken } from '~/utils/getRomeoToken'
import type { PageType } from '~/utils/urlResolver'
import { toURL, urlQuery } from '~/utils/urlResolver'

import googleReviews from '~~/appGoogleReviews.json'

import EsIndexTypes = indexes.EsIndexTypes
import RecordTypes = indexes.RecordTypes
import { useDeepCompareEffect } from 'ahooks'

import InPageNav from '~/components/blocks/InPageNav/InPageNav'
import type { ReportCardProps } from '~/components/blocks/ReportCard/ReportCard'
import { modalTypes } from '~/components/modalLoader'
import { ComponentTheme } from '~/components/themes'
import ReportsContainer from '~/containers/ReportsContainer/ReportsContainer'
import { GlobalStoreProps } from '~/contexts/GlobalStoreReducer'
import { supportedLanguages } from '~/data/lang/settings'
import { artworkQuery } from '~/data/sanity-queries/artwork-query'
import useWithAuthAnalytics from '~/hooks/useWithAuthAnalytics'
import { multiPostHogEventPusher } from '~/utils/analyticsPusher'
import { getBreadcrumbJsonLd } from '~/utils/breadcrumb'
import { addTranslations, loadMessages, type Locale } from '~/utils/i18n'
import {
  PushInteractionLocation,
  PushInteractionType,
  PushWebProduct,
} from '~/utils/posthogEvents'
import { getSEOMetaByType, MetaTemplate } from '~/utils/seo'
import { withLocalizedSettings } from '~/utils/withLocalizedSettings'

const VideoModule = dynamic(
  () => import('~/components/blocks/VideoSlider/VideoSlider')
)

export type ArtworkParams = {
  artist: string
  slug: string
  artworkSlug: string
  lang: string
  collection?: string
  collectionTaxonomy?: string
}

export interface PDPPageData {
  heroProps: PDPHeroProps
  banner: KeyMessageBannerProps
  artworkId: string
  video?: {
    title: string
    videoUrl: string
    image: ImageProps
    watchTime?: string
  }
  fallbackRelatedArtworks: ArtworkCardProps[]
  auctionHistory: AuctionHistoryItem[]
  artist: {
    _id: string
    slug: string
    about?: Block[]
  }
  formatId: string
  collection: {
    _id: string
    title: string
    slug: string
    collectionTaxonomy?: string
    relatedCollectionArtworks: ArtworkCardProps[]
  }
  longDescription?: Block[]
  longDescription1?: Block[]
  longDescription2?: Block[]
  breadcrumb: BreadcrumbProps['list']
  preview: boolean
  queryParams: any
  body: Array<any>
  valueText?: Block[]
  howToBuy?: {
    buying?: any
    selling?: any
  }
  featuredReports?: ReportCardProps[]
}

const getLD = (data: any) => {
  const ldImage = sanityUrl(data.heroProps.image.asset)
    .auto('format')
    .url()
    ?.toString()
  return { ldImage }
}

const sortData = (data: any) => {
  data['overrideSlugTitle'] = `${data.heroProps.title} ${data.heroProps.format}`
  data['overrideCollectionTitle'] = `${data.collection.title}`
}

const artworksSliderEvents = {
  artwork: {
    method: SearchAnalystMethods.clickedObjectIDs,
    name: SearchAnalystEventNames.artworkCollectionArtworkClicked,
  },
  artist: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.artworkCollectionArtworkArtistClicked,
  },
}
const relatedArtworksEvents = {
  artwork: {
    method: SearchAnalystMethods.clickedObjectIDs,
    name: SearchAnalystEventNames.artworkRelatedArtworkClicked,
  },
  artist: {
    method: SearchAnalystMethods.clickedFilters,
    name: SearchAnalystEventNames.artworkRelatedArtworkArtistClicked,
  },
}

export const PDP: NextPage<PDPPageData> = (props: PDPPageData) => {
  const tArtworkPage = useTranslations('artworkPage')
  const tCommon = useTranslations('common')
  const {
    banner,
    artworkId,
    artist,
    formatId,
    longDescription,
    longDescription1,
    longDescription2,
    collection,
    fallbackRelatedArtworks,
    auctionHistory,
    valueText,
    body,
    howToBuy,
    video,
    featuredReports,
    breadcrumb,
  } = props
  const { sendSearchInsightsViewEvent } = useSearchAnalyst()
  const { handleArtistEventWithAuthAnalytics } = useWithAuthAnalytics()
  const { pdpMainVideo } = useSettingsContext()
  const { stateDispatch } = useContext(GlobalStoreContext)
  const locale = useLocale()
  const videos = []
  if (video) videos.push(video)

  const heroProps = {
    ...props.heroProps,
    artworkId: artworkId,
    howToBuy,
    valueText,
  }

  if (video) {
    heroProps.videoModule = {
      video: video,
      title: tArtworkPage('watchOurVideo'),
    } as any
  }

  if (auctionHistory && auctionHistory.length > 0) {
    heroProps.auctionHistory = auctionHistory[0]
  }

  useDeepCompareEffect(() => {
    sendSearchInsightsViewEvent(SearchAnalystMethods.viewedObjectIDs, {
      eventName: SearchAnalystEventNames.artworkPageViewed,
      indexType: EsIndexTypes.ARTWORKS,
      objectIDs: [artworkId],
    })

    handleArtistEventWithAuthAnalytics(
      'PDP Page Loaded',
      {
        webProduct: PushWebProduct.PDP,
        interactionType: PushInteractionType.PageLoad,
        interactionLocation: PushInteractionLocation.PDPPage,
      },
      {
        artistName: heroProps?.artist?.name,
        artworkName: props.heroProps.title,
      }
    )
  }, [artworkId, heroProps])

  // Handle PDP main video modal
  useEffect(() => {
    if (
      !localStorage.getItem('hasViewedPdp') &&
      pdpMainVideo &&
      heroProps?.artist?.tierLevel === 1
    ) {
      // Map video object properties to WatchVideoModal props
      const modalProps = {
        ...pdpMainVideo,
        videoUrl: pdpMainVideo?.videoUrl,
        title: pdpMainVideo?.title,
        modalTitle: tCommon('howItWorksVideoTitle'),
        description: pdpMainVideo?.keySummary || pdpMainVideo?.summary,
        image: pdpMainVideo?.coverImage,
        hideHeader: true,
        fixedBottomMobile: true,
      }

      stateDispatch({
        type: GlobalStoreProps.UPDATE_MODAL_STATE,
        payload: {
          isOpen: true,
          component: modalTypes.watchVideoModal,
          content: null,
          props: modalProps,
        },
      })

      // Set localStorage to true after opening modal
      localStorage.setItem('hasViewedPdp', 'true')
    }
  }, [pdpMainVideo, stateDispatch])

  let content
  let intro

  if (longDescription1 && longDescription2) {
    intro = longDescription1
    content = longDescription2
  } else {
    intro = longDescription as Block[]
    content = longDescription as Block[]
    if (longDescription && longDescription.length > 0) {
      intro = longDescription.slice(0, 1)

      if (intro[0].style !== 'h2') {
        intro = longDescription.slice(0, 1)
        content = longDescription.slice(1)
      } else {
        intro = longDescription.slice(1, 2)
        content = longDescription.slice(2)
      }
    }
  }

  const catalogueLinks = [
    {
      url: toURL({
        slug: artist?.slug,
        _type: 'artist',
      }),
      linkText: tArtworkPage('readMoreAboutArtist', {
        artistName: heroProps?.artist?.name,
      }),
      type: RecordTypes.ARTIST,
      id: artist?._id,
    },
  ]

  if (collection.slug) {
    catalogueLinks.unshift({
      url: toURL({
        artist: artist.slug,
        slug: collection.slug,
        collectionTaxonomy: collection.collectionTaxonomy,
        _type: 'collection',
      }),
      linkText: tArtworkPage('readMoreAboutCollection', {
        collectionTitle: collection.title,
      }),
      type: RecordTypes.COLLECTION,
      id: collection._id,
    })
  }

  const { currentArtworkDispatch } = useContext(SelectedArtworkContext)
  const setCurrentArt = (payload: any) => {
    currentArtworkDispatch({
      type: CurrentArtworkProps.UPDATE_SELECTED_ARTWORK,
      payload,
    })
  }

  const { sendSearchInsightsEvent } = useSearchAnalyst()

  const setArtwork = useCallback(
    async (type: FormTypes) => {
      const eventName =
        type === FormTypes.buy
          ? SearchAnalystEventNames.artworkEnquiryToBuyClicked
          : SearchAnalystEventNames.artworkEnquiryToSellClicked

      sendSearchInsightsEvent(SearchAnalystMethods.convertedObjectIDs, {
        eventName,
        indexType: EsIndexTypes.ARTWORKS,
        objectIDs: [artworkId],
      })

      await multiPostHogEventPusher([
        {
          eventType: eventName,
        },
      ])

      setCurrentArt({
        artist: {
          ...artist,
          tierLevel: heroProps.artist.tierLevel,
        },
        selectedArtwork: {
          name: heroProps.title,
          id: artworkId,
          format: heroProps.format || '',
          genre: heroProps.genre,
          minValue: heroProps?.listPriceMin,
          maxValue: heroProps?.listPriceMax,
          poa: heroProps?.poa,
          sellTicketsCount: heroProps?.sellTicketsCount,
          ownedTicketsCount: heroProps?.ownedTicketsCount,
          buyTicketsCount: heroProps?.buyTicketsCount,
        },
      })
    },
    [artworkId, artist, heroProps.title, heroProps.format, heroProps.genre]
  )

  const jumpLinks = []
  if (auctionHistory && auctionHistory.length > 0) {
    jumpLinks.push({
      title: tArtworkPage('trackValueTrend'),
      slug: 'auction-history-graph',
    })
    jumpLinks.push({
      // Labelled "Live Market" — anchors to the live market band inside AuctionHistory
      title: tArtworkPage('auctionHistory'),
      slug: 'live-market',
    })
  }
  if (intro && content) {
    jumpLinks.push({
      title: tCommon('meaningAnalysis'),
      slug: 'meaning-analysis',
    })
  }
  if (
    collection._id &&
    collection.relatedCollectionArtworks &&
    collection.relatedCollectionArtworks.length > 0
  ) {
    jumpLinks.push({
      // Figma sticky nav (14116-22884) labels this section "Related Works"
      title: tArtworkPage('relatedWorks'),
      slug: 'more-from-collection',
    })
  }
  return (
    <>
      <Breadcrumb list={breadcrumb} preserveCaps={true} />
      {/* Hero owns its own gutters and max width — it spans wider than the
          standard Container on large screens (Figma 14371-16788) */}
      <Container className="noMaxWidth" gutters={gutterTypes.never}>
        <PDPHero {...heroProps} setArtwork={setArtwork} videos={videos} />
      </Container>
      {jumpLinks.length > 0 && (
        <InPageNav
          items={jumpLinks}
          title={''}
          idAppend=""
          bordered
          center
          noMarginTop
          footerComponent={undefined}
        />
      )}
      {auctionHistory && auctionHistory.length > 0 && (
        <div id="auction-history">
          <AuctionHistory
            usePostHogAnalytics
            artworkId={+artworkId.split('-')[1]}
            auctionHistory={auctionHistory}
            artist={heroProps.artist.name}
            artwork={heroProps.title}
            artworkImage={heroProps.image}
            artworkFormat={heroProps.format}
            listPriceMin={heroProps.listPriceMin}
            listPriceMax={heroProps.listPriceMax}
            listPriceMinValues={heroProps.listPriceMinValues}
            listPriceMaxValues={heroProps.listPriceMaxValues}
            poa={heroProps.poa}
            buyTicketsCount={heroProps.buyTicketsCount}
            sellTicketsCount={heroProps.sellTicketsCount}
            ownedTicketsCount={heroProps.ownedTicketsCount}
            key={artworkId}
            setArtwork={setArtwork}
            summary={valueText}
            tableContained
          />
        </div>
      )}
      {intro && content && (
        <div id="meaning-analysis">
          <CatalogueEssay
            title={tCommon('meaningAnalysis')}
            intro={intro}
            content={content}
            artist={{
              about: artist.about,
              name: heroProps.artist.name,
            }}
            links={catalogueLinks}
            valueText={undefined}
          />
        </div>
      )}
      {video && (
        <VideoModule
          video={[video] as any}
          flip={false}
          title={tArtworkPage('watchOurVideo')}
          moduleId="videoModule"
          theme={ComponentTheme.tertiary}
        />
      )}
      {body && body.map((block, i) => resolveBlock(block, i))}
      {collection._id &&
        collection.relatedCollectionArtworks &&
        collection.relatedCollectionArtworks.length > 0 && (
          <div id="more-from-collection">
            <VerticalPadder>
              <ArtworksSlider
                artworks={collection.relatedCollectionArtworks}
                viewAlleventName={`More from the ${
                  collection.collectionTaxonomy
                    ? collection.collectionTaxonomy
                    : 'collection'
                } view all button clicked`}
                title={tArtworkPage('moreFromCollection', {
                  collectionTitle: collection.title,
                })}
                searchInsightsEvents={artworksSliderEvents}
                viewAllLink={toURL({
                  artist: artist.slug,
                  slug: collection.slug,
                  collectionTaxonomy: collection.collectionTaxonomy,
                  _type: 'collection',
                })}
                noArtistLink={true}
              />
            </VerticalPadder>
          </div>
        )}
      <RelatedArtworks
        artistId={artist._id}
        formatId={formatId}
        fallbackData={fallbackRelatedArtworks}
        // year={parseInt(heroProps.year)}
        artworkId={artworkId}
        excludeCollection={collection._id}
        searchInsightsEvents={relatedArtworksEvents}
        showAllLink
        relatedArtist={{ name: heroProps.artist.name, slug: artist.slug }}
        title={tArtworkPage('moreFromArtist', {
          artistName: heroProps.artist.name,
        })}
        noArtistLink={true}
      />
      {featuredReports && locale == 'en' && (
        <ReportsContainer
          reports={featuredReports}
          title={`Featured reports`}
          slider
          consise
          theme={ComponentTheme.secondary}
          viewAllLink
        />
      )}
      {banner && banner?.images && (
        <KeyMessageBanner
          {...banner}
          imageAlign={ImageAlignmentVariants.full}
        />
      )}
    </>
  )
}

export const pdpGetStaticProps = async (
  context: GetStaticPropsContext<ArtworkParams>,
  localizedData?: {
    settings: any
    allArtists: any
    totalArtists: number
    seoTemplates: any
  }
): Promise<GetStaticPropsResult<PDPPageData>> => {
  try {
    const artistSlug = context?.params?.slug
    const artworkSlug = context?.params?.artworkSlug
    const lang = (context?.params?.lang as Locale) || 'en'
    const messages = await loadMessages(lang)

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const { client, limiter, scorer } = await useSanity()
    const data = await client.fetch(artworkQuery(limiter, scorer, lang), {
      slug: artworkSlug,
      artistSlug,
      lang,
    })

    if (data === null) {
      throw new Error('No data found')
    }

    const awId = data?.artworkId?.includes('-')
      ? data.artworkId.split('-')[1]
      : null

    let romeoData = null
    if (awId && process.env.ROMEO_VAULT_URL) {
      console.log(`Fetching Romeo data for artwork ID: ${awId}`)
      const romeoBaseUrl = process.env.ROMEO_VAULT_URL

      const apiUsername = process.env.ROMEO_USER
      const apiPassword = process.env.ROMEO_PASSWORD

      // Skip Romeo if credentials are missing
      if (!apiUsername || !apiPassword) {
        console.warn('Romeo credentials not configured, skipping auction data')
        romeoData = null
      } else {
        try {
          // Reuse a cached Romeo token where possible to avoid minting a fresh
          // token for every page during a build.
          const access = await getRomeoToken({
            baseUrl: romeoBaseUrl,
            username: apiUsername,
            password: apiPassword,
          })

          // Create another timeout for the data fetch
          const dataTimeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Romeo API timeout')), 8000)
          })

          const romeoQuery = (await Promise.race([
            fetch(`${romeoBaseUrl}/artwork/api/v2/summary/${awId}`, {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json',
                Authorization: `Bearer ${access}`,
              },
            }),
            dataTimeoutPromise,
          ])) as Response

          const auctionData =
            romeoQuery.status === 200 ? await romeoQuery.json() : null

          romeoData = auctionData?.auction_history?.map(
            (item: any, key: any) => {
              return {
                ...item,
                prices: key === 0 ? auctionData.most_recent_price : undefined,
              }
            }
          )
        } catch (error: any) {
          if (error.message === 'Romeo API timeout') {
            console.error('Romeo API request timed out after 8 seconds')
          } else {
            console.error('Error fetching Romeo data:', error.message || error)
          }
          // gracefully handle the error to prevent the build breaking
          romeoData = null
        }
      }
    }

    const auctionHistory = Array.isArray(romeoData)
      ? romeoData.map(
          (item: any): AuctionHistoryItem => ({
            auctionDate: item?.date_of_sale || null,
            artworkName: `${data.heroProps?.title || ''} - ${
              data.heroProps?.format || ''
            }`,
            auctionHouse: `${item?.auction_house_name || ''} - ${
              item?.auction_house_country || ''
            }`,
            prices: item?.prices || [],
          })
        )
      : []

    const { ldImage } = getLD(data)
    sortData(data)

    const newBody =
      data.body && auctionHistory
        ? await getPageBuilderArtistIndexData(data)
        : []

    data.body = newBody

    const overrideSlugTitle = data?.overrideSlugTitle

    // Helper to add locale prefix
    const addLocalePrefix = (path: string) => {
      return lang === 'en' ? path : joinURL('/', lang, path)
    }

    // Forgetting the build breadcrumb dynamic for now - can just build the hardocded references within here
    const breadcrumb: BreadcrumbProps['list'] | undefined = [
      {
        title: messages?.breadcrumb?.artForSale || 'Art for sale',
        url: addLocalePrefix(getLocalizedHardCodedSlug('artworks', lang)),
        item: getLocalizedHardCodedSlug('artworks', lang).replace('/', ''),
      },
      {
        title: data.heroProps.artist.name,
        url: addLocalePrefix(`/artist-${data.artist.slug}`),
        item: data.artist.slug,
      },
    ]

    // Add collection to breadcrumb if it exists
    if (data.collection?.slug && data.collection?.title) {
      breadcrumb.push({
        title: data.collection.title,
        url: addLocalePrefix(
          `/artist-${data.artist.slug}/${data.collection.collectionTaxonomy || 'collection'}-${data.collection.slug}`
        ),
        item: data.collection.slug,
      })
    }

    // Add artwork title as the final breadcrumb
    breadcrumb.push({
      title: overrideSlugTitle,
      url: '', // Current page, no URL needed
      item: data.heroProps.title,
    })
    const breadcrumbJsonLd = breadcrumb ? getBreadcrumbJsonLd(breadcrumb) : []

    const meta = await getSEOMetaByType(
      MetaTemplate.Artwork,
      data,
      localizedData?.settings as unknown as SiteSettings,
      undefined,
      lang
    )

    return {
      props: {
        notFound: Object.keys(data).length < 1,
        ...data,
        meta,
        auctionHistory,
        hasGoogleReviews: true,
        queryParams: { slug: artworkSlug },
        preview: !!context.preview,
        breadcrumb,
        breadcrumbJsonLd,
        jsonLd: {
          productJsonLd: {
            name: data.heroProps.title,
            images: [ldImage],
            description: toPlainText(
              data.longDescription1 ?? data.longDescription?.slice(1, 2)
            ),
            brand: data.heroProps.artist.name,
            ratingValue: googleReviews.averageRating,
            reviewCount: googleReviews.totalReviewCount,
          },
          breadcrumbJsonLd: true,
          productTableJsonLd: {
            name: data.heroProps.title,
            tableItems: [
              { name: 'Medium', value: data.heroProps.medium },
              { name: 'Format', value: data.heroProps.format },
              { name: 'Year', value: data.heroProps.year },
              {
                name: 'Dimensions',
                value: getDimensionString(data.heroProps.dimensions),
              },
              { name: 'Sizes', value: data.heroProps.editionSize },
              {
                name: 'Signed',
                value: data.heroProps.signed === 'signed' ? 'Yes' : 'No',
              },
              {
                name: 'Condition',
                value:
                  data.heroProps.condition !== null
                    ? data.heroProps.condition
                    : '',
              },
              {
                name: 'Series',
                value: data.collection ? 'Yes' : 'No',
              },
            ],
          },
          // speakableJsonLd: {
          //   name: data.heroProps.title,
          //   target: {
          //     cssSelector: ['.json-ld-speakable'],
          //   },
          // },
          // faqJsonLd: data.longDescription,
        },
      },
    }
  } catch (e) {
    console.log('e', e)
    if (context.params) {
      // 301 redirect to collection page if exists
      if (context.params.slug && context.params.collection) {
        return {
          redirect: {
            permanent: true,
            destination: toURL({
              _type: 'collection',
              slug: context.params.collection,
              collectionTaxonomy: context.params.collectionTaxonomy,
              artist: context.params.slug,
            }),
          },
        }
      }
      // 301 redirect to artist page if collection does not exist
      if (context.params.slug) {
        return {
          props: {} as any,
          redirect: {
            permanent: true,
            destination: toURL({
              _type: 'artist',
              slug: context.params.slug,
            }),
          },
        }
      }
    }

    return {
      notFound: true,
    }
  }
}

// Wrapper that calls pdpGetStaticProps with localizedData
const getStaticPropsOriginal = async (
  context: GetStaticPropsContext<ArtworkParams>,
  localizedData: {
    settings: any
    allArtists: any
    totalArtists: number
    seoTemplates: any
  }
): Promise<GetStaticPropsResult<PDPPageData>> => {
  return pdpGetStaticProps(context, localizedData)
}

// Add SSR translation support
export const getStaticProps = withLocalizedSettings(
  async (context, localizedData) => {
    const typedContext = context as GetStaticPropsContext<ArtworkParams>
    const result = await getStaticPropsOriginal(typedContext, localizedData)

    // Check if it's a redirect or notFound result - same pattern as collection page
    if ('redirect' in result || 'notFound' in result) {
      // During static build, return notFound to avoid Next.js build errors
      // During runtime, redirects will work normally for better UX
      if ('redirect' in result) {
        const isStaticBuild =
          process.env.NODE_ENV === 'production' && !process.env.VERCEL_URL

        if (isStaticBuild) {
          return { notFound: true }
        }
      }

      return result
    }

    if ('props' in result) {
      return {
        props: await addTranslations(result.props, context),
      }
    }

    return result
  }
)

export const getStaticPaths: GetStaticPaths = async () => {
  const limiter =
    process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_REF === 'main'
      ? '[0..10]'
      : '[0..10]'

  const pages = await sanityClientCdn.fetch<Array<{ url: PageType }>>(
    `*[(_type in $pageTypes && _type != 'artist' && defined(slug.current) && (!defined(isVisible) || isVisible == true) && !(_id in path('drafts.**')))]
    | score(
      boost(_type match "artwork", 2))
    | order(_score desc)
      ${limiter} {
      ${urlQuery}
    }`,
    {
      pageTypes: ['artwork'],
    }
  )

  const pagesToBuild = pages

  const paths = (pagesToBuild?.length ? pagesToBuild : [])
    .filter(p => !['/', '404', 'artworks', 'search'].includes(p.url.slug))
    .flatMap(p => {
      const getBasePath = (lang: string) => {
        const baseParams = {
          lang,
          slug: p.url.artist,
          artworkSlug: p.url.slug,
        }

        if (p.url.collection && p.url.collectionTaxonomy) {
          return {
            params: {
              ...baseParams,
              collectionTaxonomy: p.url.collectionTaxonomy,
              collection: p.url.collection,
            },
          }
        }

        return {
          params: baseParams,
        }
      }

      const basePath = getBasePath('en')

      const languagePaths = supportedLanguages.map(lang => getBasePath(lang))

      return [basePath, ...languagePaths].filter(
        path => path.params.slug && path.params.artworkSlug
      )
    })

  return {
    paths: paths,
    fallback: 'blocking',
  }
}

export default PDP
